import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select } from "@/components/base/FormControls";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_DOCUMENT_TYPE_OPTIONS,
  agenceContratReference,
  agenceLocataireNomComplet,
  formatAgenceFileSize,
  type AgenceDocument,
  type AgenceDocumentInput,
  type AgenceDocumentType,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";

interface DocumentFormProps {
  open: boolean;
  onClose: () => void;
  defaultProprietaireId?: string;
  defaultImmeubleId?: string;
  defaultBienId?: string;
  initial?: AgenceDocument;
}

interface FormState {
  nom: string;
  type: AgenceDocumentType;
  date: string;
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  contratId: string;
  locataireId: string;
}

interface SelectedFile {
  url: string;
  nom: string;
  type: string;
  taille: number;
}

const EMPTY_STATE: FormState = {
  nom: "",
  type: "contrat",
  date: "",
  proprietaireId: "",
  immeubleId: "",
  bienId: "",
  contratId: "",
  locataireId: "",
};

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
    reader.readAsDataURL(file);
  });
}

export default function DocumentForm({
  open,
  onClose,
  defaultProprietaireId,
  defaultImmeubleId,
  defaultBienId,
  initial,
}: DocumentFormProps) {
  const { immeubles, biens, contrats, locataires, getBien, addDocument, updateDocument } = useAgenceData();
  const { proprietaires } = useProprietaires();

  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [file, setFile] = useState<SelectedFile | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        nom: initial.nom,
        type: initial.type,
        date: initial.date,
        proprietaireId: initial.proprietaireId,
        immeubleId: initial.immeubleId,
        bienId: initial.bienId,
        contratId: initial.contratId,
        locataireId: initial.locataireId,
      });
      setFile({
        url: initial.fichierUrl,
        nom: initial.fichierNom,
        type: initial.fichierType,
        taille: initial.fichierTaille,
      });
      setErrors({});
      return;
    }
    setForm(() => {
      const base: FormState = { ...EMPTY_STATE, date: new Date().toISOString().slice(0, 10) };
      if (defaultBienId) {
        const bien = getBien(defaultBienId);
        if (bien) {
          base.bienId = bien.id;
          base.immeubleId = bien.immeubleId;
          base.proprietaireId = bien.proprietaireId;
        }
      }
      if (defaultImmeubleId && !base.immeubleId) {
        base.immeubleId = defaultImmeubleId;
        const immeuble = immeubles.find((item) => item.id === defaultImmeubleId);
        if (immeuble) base.proprietaireId = immeuble.proprietaireId;
      }
      if (defaultProprietaireId && !base.proprietaireId) base.proprietaireId = defaultProprietaireId;
      return base;
    });
    setFile(null);
    setErrors({});
  }, [open, defaultProprietaireId, defaultImmeubleId, defaultBienId, getBien, immeubles]);

  const proprietaireOptions = useMemo(
    () => proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    [proprietaires]
  );

  const immeubleOptions = useMemo(
    () =>
      immeubles
        .filter((immeuble) => !form.proprietaireId || immeuble.proprietaireId === form.proprietaireId)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    [immeubles, form.proprietaireId]
  );

  const bienOptions = useMemo(
    () =>
      biens
        .filter((bien) => !form.immeubleId || bien.immeubleId === form.immeubleId)
        .map((bien) => ({
          value: bien.id,
          label: `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    [biens, form.immeubleId]
  );

  // Listes issues du périmètre RLS de l'utilisateur (contrats/locataires de son
  // organisation uniquement) : aucune donnée d'un autre périmètre n'est chargée.
  const contratOptions = useMemo(
    () =>
      contrats.map((contrat) => {
        const locataire = locataires.find((item) => item.id === contrat.locataireId);
        const bien = contrat.bienId ? getBien(contrat.bienId) : undefined;
        const label = [
          agenceContratReference(contrat.id),
          locataire ? agenceLocataireNomComplet(locataire) : "",
          bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}` : "",
        ]
          .filter(Boolean)
          .join(" · ");
        return { value: contrat.id, label };
      }),
    [contrats, locataires, getBien]
  );

  const locataireOptions = useMemo(
    () => locataires.map((locataire) => ({ value: locataire.id, label: agenceLocataireNomComplet(locataire) })),
    [locataires]
  );

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value } as FormState;
      if (key === "proprietaireId") {
        next.immeubleId = "";
        next.bienId = "";
      }
      if (key === "immeubleId") {
        next.bienId = "";
        const immeuble = immeubles.find((item) => item.id === value);
        if (immeuble) next.proprietaireId = immeuble.proprietaireId;
      }
      if (key === "bienId") {
        const bien = getBien(value);
        if (bien) {
          next.immeubleId = bien.immeubleId;
          next.proprietaireId = bien.proprietaireId;
        }
      }
      return next;
    });
  };

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const url = await readFileAsDataUrl(selected);
    setFile({ url, nom: selected.name, type: selected.type, taille: selected.size });
    if (!form.nom.trim()) {
      setForm((prev) => ({ ...prev, nom: selected.name.replace(/\.[^.]+$/, "") }));
    }
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setFile(null);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.nom.trim()) nextErrors.nom = "Donnez un nom au document.";
    if (!form.date) nextErrors.date = "Indiquez la date du document.";
    if (!file) nextErrors.fichier = "Sélectionnez un fichier à ajouter.";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !file) return;

    const input: AgenceDocumentInput = {
      nom: form.nom.trim(),
      type: form.type,
      date: form.date,
      proprietaireId: form.proprietaireId,
      immeubleId: form.immeubleId,
      bienId: form.bienId,
      contratId: form.contratId,
      locataireId: form.locataireId,
      fichierUrl: file.url,
      fichierNom: file.nom,
      fichierType: file.type,
      fichierTaille: file.taille,
    };

    if (initial) {
      updateDocument(initial.id, input);
    } else {
      addDocument(input);
    }
    resetAndClose();
  };

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier le document" : "Ajouter un document"}
      description="Centralisez contrats, pièces d'identité, documents des biens, reçus et factures. Le nom, la date et le fichier sont obligatoires."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="agence-document-form"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer le document
          </button>
        </>
      }
    >
      <form id="agence-document-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field label="Nom du document" htmlFor="agence-doc-nom" required error={errors.nom} className="md:col-span-2">
            <Input
              id="agence-doc-nom"
              value={form.nom}
              onChange={(event) => update("nom", event.target.value)}
              placeholder="Ex. Contrat de bail — Appartement B2"
              invalid={Boolean(errors.nom)}
              maxLength={140}
            />
          </Field>

          <Field label="Type" htmlFor="agence-doc-type" required>
            <Select
              id="agence-doc-type"
              value={form.type}
              onChange={(event) => update("type", event.target.value)}
              options={AGENCE_DOCUMENT_TYPE_OPTIONS}
            />
          </Field>

          <Field label="Date" htmlFor="agence-doc-date" required error={errors.date}>
            <Input
              id="agence-doc-date"
              type="date"
              value={form.date}
              onChange={(event) => update("date", event.target.value)}
              invalid={Boolean(errors.date)}
            />
          </Field>

          <Field label="Propriétaire" htmlFor="agence-doc-proprietaire" hint="Facultatif, filtre les immeubles.">
            <Select
              id="agence-doc-proprietaire"
              value={form.proprietaireId}
              onChange={(event) => update("proprietaireId", event.target.value)}
              options={proprietaireOptions}
              placeholder={
                proprietaireOptions.length > 0 ? "Aucun propriétaire en particulier" : "Aucun propriétaire créé"
              }
              disabled={proprietaireOptions.length === 0}
            />
          </Field>

          <Field label="Immeuble" htmlFor="agence-doc-immeuble" hint="Facultatif.">
            <Select
              id="agence-doc-immeuble"
              value={form.immeubleId}
              onChange={(event) => update("immeubleId", event.target.value)}
              options={immeubleOptions}
              placeholder={immeubleOptions.length > 0 ? "Aucun immeuble en particulier" : "Aucun immeuble disponible"}
              disabled={immeubleOptions.length === 0}
            />
          </Field>

          <Field label="Bien" htmlFor="agence-doc-bien" hint="Facultatif, filtré selon l'immeuble.">
            <Select
              id="agence-doc-bien"
              value={form.bienId}
              onChange={(event) => update("bienId", event.target.value)}
              options={bienOptions}
              placeholder={bienOptions.length > 0 ? "Aucun bien en particulier" : "Aucun bien disponible"}
              disabled={bienOptions.length === 0}
            />
          </Field>

          <Field label="Contrat" htmlFor="agence-doc-contrat" hint="Facultatif.">
            <Select
              id="agence-doc-contrat"
              value={form.contratId}
              onChange={(event) => update("contratId", event.target.value)}
              options={contratOptions}
              placeholder={contratOptions.length > 0 ? "Aucun contrat en particulier" : "Aucun contrat disponible"}
              disabled={contratOptions.length === 0}
            />
          </Field>

          <Field label="Locataire" htmlFor="agence-doc-locataire" hint="Facultatif.">
            <Select
              id="agence-doc-locataire"
              value={form.locataireId}
              onChange={(event) => update("locataireId", event.target.value)}
              options={locataireOptions}
              placeholder={
                locataireOptions.length > 0 ? "Aucun locataire en particulier" : "Aucun locataire disponible"
              }
              disabled={locataireOptions.length === 0}
            />
          </Field>

          <Field
            label="Fichier"
            htmlFor="agence-doc-fichier"
            required
            error={errors.fichier}
            hint="PDF, image ou tout autre type de fichier (aperçu local, l'envoi réel viendra avec le stockage)."
            className="md:col-span-2"
          >
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-upload-2-line text-base" aria-hidden="true" />
                  Choisir un fichier
                </button>
                {file && (
                  <span className="flex min-w-0 items-center gap-1.5 text-xs text-foreground-600">
                    <i className="ri-attachment-2 shrink-0 text-sm text-foreground-400" aria-hidden="true" />
                    <span className="truncate">{file.nom}</span>
                    <span className="shrink-0 text-foreground-400">· {formatAgenceFileSize(file.taille)}</span>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      aria-label="Retirer le fichier"
                      className="shrink-0 text-primary-700 hover:text-primary-800 cursor-pointer"
                    >
                      <i className="ri-close-line text-sm" aria-hidden="true" />
                    </button>
                  </span>
                )}
              </div>
              <input ref={fileRef} id="agence-doc-fichier" type="file" onChange={handleFile} className="hidden" />
            </div>
          </Field>
        </div>
      </form>
    </Modal>
  );
}